import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { useUrlFilters } from '../../utils/useUrlFilters';
import { BUDGET_FILTER_DEFINITIONS } from '../../utils/useUrlFilters';
import { getCurrentFinancialYear, getFinancialYearMonths, getFinancialYearLabel } from '../../utils/financialYear';
import { formatCurrency, formatCurrencyCompact } from '../../utils/currency';
import Dropdown from '../../components/Dropdown';
import { DropdownItem } from '../../components/Dropdown/config';
import SearchableClientDropdown from '../../components/SearchableClientDropdown';
import WorkflowStatusPill from '../../components/WorkflowStatusPill';
import ListPage, { FilterTrigger, SearchInput, StatusSegments, useNarrowViewport } from '../../components/ListPage';
import JobCardModal from '../JobCards/components/JobCardModal';
import { useRestoredJobCardModal } from '../JobCards/components/JobCardModal/restore';
import { JobCard } from '../JobCards/config';
import { useBudgetData } from './useBudgetData';
import { BudgetRow, MonthlyAmount, PROJECT_TYPE_OPTIONS, BudgetFilters, ProjectTeamMember, TERMINAL_STATUSES } from './config';
import MonthlyCell from './components/MonthlyCell';
import BudgetTotalRow from './components/BudgetTotalRow';
import AggregateSection from './components/AggregateSection';
import './styles.scss';

interface TeamMemberOption {
  id: string;
  first_name: string;
  last_name: string;
}

interface WorkflowStatusOption {
  id: number;
  status_name: string;
}

interface WorkflowStatusTypeRow {
  job_type: string;
  display_order: number;
  workflow_statuses: { id: number; status_name: string } | null;
}

// Budget is a Design/Advisory view (RWC and RWD only appear as the aggregate
// rows at the bottom), so the status filter offers just those two workflows'
// statuses, in workflow display order. A status shared by both types keeps
// its LATEST position (max display_order) so Complete/Archived sort last;
// ties keep the Design sequence intact.
const mergeStatusOptions = (rows: WorkflowStatusTypeRow[]): WorkflowStatusOption[] => {
  const merged = new Map<
    number,
    { id: number; status_name: string; order: number; inDesign: boolean }
  >();
  for (const row of rows) {
    const ws = row.workflow_statuses;
    if (!ws) continue;
    const cur = merged.get(ws.id);
    if (!cur) {
      merged.set(ws.id, {
        id: ws.id,
        status_name: ws.status_name,
        order: row.display_order,
        inDesign: row.job_type === 'Design',
      });
    } else {
      cur.order = Math.max(cur.order, row.display_order);
      cur.inDesign = cur.inDesign || row.job_type === 'Design';
    }
  }
  return Array.from(merged.values())
    .sort(
      (a, b) =>
        a.order - b.order ||
        Number(b.inDesign) - Number(a.inDesign) ||
        a.status_name.localeCompare(b.status_name),
    )
    .map(({ id, status_name }) => ({ id, status_name }));
};

const SEARCH_DEBOUNCE_MS = 300;
// Sub-cell widths: Project 245 + People 165 + Probable 90 + Fee 100 = 600.
// Two-line cells: the job/client identity stacks in column 1 and the status
// pill sits above the lead + team avatars in column 2. That buys back 60px
// against the old five-column band while ALSO fitting the client and the new
// Probable figure — laying those out as two more fixed columns would have
// pushed the band past 800px and eaten two months of the scrollable grid.
// Must match --budget-label-subcols in styles.scss.
const LABEL_COLS_WIDTH = 600;
const MONTH_COL_WIDTH = 110;
const UNALLOC_COL_WIDTH = 140;
// First FY the system supports. Used to gate the one-time "Pre FY25/26"
// migration column — once a workspace moves past this FY the column is gone.
const FIRST_FY_START_YEAR = 2025;


// Quick-filter pills: the primary lifecycle stages, each expanding to a set
// of workflow statuses. Selecting one presets the (multi-select) status
// filter so rows and footer totals reflect just that group.
const STATUS_GROUPS: { label: string; statuses: string[] }[] = [
  { label: 'Probable', statuses: ['Probable'] },
  {
    label: 'Design',
    statuses: [
      'Preliminary',
      'Active',
      'Schematic Design',
      'Design Development',
      'Contract Documentation',
    ],
  },
  { label: 'Construction', statuses: ['Construction', 'Defect Liability'] },
  { label: 'Complete', statuses: TERMINAL_STATUSES },
];

// Terminal entities report unallocated = 0 (leftover fee on a finished job
// will never be billed); the optimistic recompute below keeps them at 0.
const ZERO_UNALLOC_STATUSES = new Set(TERMINAL_STATUSES);
const isCompletedRow = (r: BudgetRow): boolean =>
  ZERO_UNALLOC_STATUSES.has(r.workflow_status_name ?? '');

// A row whose ENTIRE fee is probable: nothing has been won yet, so Won Fee
// and Unallocated have no meaningful value. They render as an em dash rather
// than $0, because "$0.00" in Unallocated reads as "fully allocated, nothing
// left" — the opposite of the truth, which is "not applicable until this is
// won". Matters most on the probable jobs that already carry month
// allocations, where a $0 beside filled month cells invites the reader to
// think the row doesn't add up.
const isWhollyProbable = (r: BudgetRow): boolean =>
  Number(r.probable_fee) > 0 && Number(r.won_fee) === 0;

const getInitials = (name: string | null | undefined): string => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// How many team avatars fit beside the lead in the People sub-cell before
// they start clipping. Anything past this collapses into a "+N" chip, so an
// eight-person team reads as four faces and "+4" rather than silently losing
// members at the column edge.
//
// 4 is the measured limit, not a round number: the binding case is lead + N
// avatars + the "+N" chip, which at 4 needs exactly the sub-cell's 145px and
// at 5 needs 169px. It depends on the 20px avatar size and the 165px People
// track — change either and re-measure rather than assuming this still holds.
const MAX_TEAM_AVATARS = 4;

const getMemberInitials = (m: ProjectTeamMember): string => {
  const first = (m.first_name || '').trim();
  const last = (m.last_name || '').trim();
  if (first && last) return (first[0] + last[0]).toUpperCase();
  if (first) return first.slice(0, 2).toUpperCase();
  if (last) return last.slice(0, 2).toUpperCase();
  return '?';
};

const memberTypeClass = (type: string): string => {
  const normalised = type.toLowerCase().replace(/\s+/g, '-');
  return `is-${normalised}`;
};

const Budget: React.FC = () => {
  const supabase = useSupabaseClient();
  const { filters, setFilter, resetFilters } = useUrlFilters(BUDGET_FILTER_DEFINITIONS);
  const isNarrow = useNarrowViewport();

  const fyStartYear =
    typeof filters.fy === 'number' && filters.fy !== null ? filters.fy : getCurrentFinancialYear();
  const projectTypes = (filters.projectTypes as string[]) || [];
  const projectLead = (filters.projectLead as string) || '';
  const workflowStatuses = useMemo(
    () => (filters.workflowStatuses as string[]) || [],
    [filters.workflowStatuses]
  );
  const clientId = (filters.client as string) || '';
  const searchUrl = (filters.search as string) || '';

  const [searchInput, setSearchInput] = useState<string>(searchUrl);

  // Month-column filter: a fyMonths index (0–11), 'prior' for the Pre-FY column,
  // or null (off). Click a month heading to show only rows with an allocation
  // in that month. Cleared automatically when the financial year changes since
  // the columns differ.
  const [monthFilter, setMonthFilter] = useState<number | 'prior' | null>(null);
  useEffect(() => {
    setMonthFilter(null);
  }, [fyStartYear]);

  // Sync URL search → input when navigating
  useEffect(() => {
    setSearchInput(searchUrl);
  }, [searchUrl]);

  // Debounce search input → URL
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchUrl) {
        setFilter('search', searchInput);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, searchUrl, setFilter]);

  // Build the filters object for the data hook
  const typesKey = projectTypes.join(',');
  const statusesKey = workflowStatuses.join(',');
  const dataFilters: BudgetFilters = useMemo(
    () => ({
      fyStartYear,
      projectTypes: typesKey ? typesKey.split(',') : [],
      projectLead,
      workflowStatuses: statusesKey ? statusesKey.split(',') : [],
      clientId,
      search: searchUrl,
    }),
    [fyStartYear, typesKey, projectLead, statusesKey, clientId, searchUrl],
  );

  const { data, loading, error, setData, refetch } = useBudgetData(dataFilters);

  // Load filter options (project leads + workflow statuses) on mount
  const [teamMembers, setTeamMembers] = useState<TeamMemberOption[]>([]);
  const [workflowStatusOptions, setWorkflowStatusOptions] = useState<WorkflowStatusOption[]>([]);

  useEffect(() => {
    const load = async () => {
      const [tmRes, wsRes] = await Promise.all([
        supabase
          .from('team_members')
          .select('id, first_name, last_name')
          .order('first_name'),
        supabase
          .from('workflow_status_types')
          .select('job_type, display_order, workflow_statuses (id, status_name)')
          .in('job_type', ['Design', 'Advisory']),
      ]);
      if (tmRes.data) setTeamMembers(tmRes.data as TeamMemberOption[]);
      if (wsRes.data) {
        setWorkflowStatusOptions(
          mergeStatusOptions(wsRes.data as unknown as WorkflowStatusTypeRow[]),
        );
      } else if (wsRes.error) {
        // Status pills/dropdown stay disabled-empty; log so it's diagnosable.
        console.error('Failed to load workflow status filter options:', wsRes.error);
      }
    };
    load();
  }, [supabase]);

  // Drop URL-carried status ids that aren't in the Design/Advisory option
  // set (e.g. an RWC-only status bookmarked before the filter narrowed) —
  // otherwise they filter the grid invisibly while the dropdown shows
  // "All Statuses" with no way to see or clear them.
  useEffect(() => {
    if (workflowStatusOptions.length === 0) return;
    const valid = new Set(workflowStatusOptions.map((ws) => String(ws.id)));
    const kept = workflowStatuses.filter((id) => valid.has(id));
    if (kept.length !== workflowStatuses.length) {
      setFilter('workflowStatuses', kept);
    }
  }, [workflowStatusOptions, workflowStatuses, setFilter]);

  const fyMonths = useMemo(() => getFinancialYearMonths(fyStartYear), [fyStartYear]);
  // The "Pre FY25/26" column only appears on the migration FY itself —
  // afterwards the data still feeds unallocated math but the affordance
  // disappears from the UI.
  const showPriorColumn = fyStartYear === FIRST_FY_START_YEAR;

  // Toggle the month-column filter: clicking the active column clears it.
  const toggleMonthFilter = useCallback((key: number | 'prior') => {
    setMonthFilter((prev) => (prev === key ? null : key));
  }, []);

  // Rows narrowed to those RELEVANT to the selected month — which means a
  // forecast allocation OR an actual invoice in it, not just the forecast.
  //
  // The point of the filter is to line up the work that touches a month, and
  // an invoiced month is the clearest case of that. Testing the forecast
  // alone hid exactly the rows the user was looking for: once a month is
  // invoiced the workflow overwrites its forecast with the actual, and any
  // row invoiced without a fee plan carries an actual with no forecast at
  // all — both drop out of a forecast-only test.
  //
  // The Pre-FY branch stays forecast-only: month 0 is a migration lump sum
  // for work completed before tracking started, and it has no actuals column
  // to pair with.
  const filteredRows = useMemo(() => {
    const rows = data?.rows ?? [];
    if (monthFilter === null) return rows;
    if (monthFilter === 'prior') return rows.filter((r) => Number(r.prior_amount) > 0);
    return rows.filter(
      (r) =>
        Number(r.forecasts[monthFilter]?.amount ?? 0) > 0 ||
        Number(r.actuals[monthFilter]?.amount ?? 0) > 0,
    );
  }, [data, monthFilter]);

  // Label for the active-filter chip (e.g. "Jul 2025" or "Pre FY25/26").
  const monthFilterLabel = useMemo(() => {
    if (monthFilter === null) return null;
    if (monthFilter === 'prior') return 'Pre FY25/26';
    const m = fyMonths[monthFilter];
    return m ? `${m.label} ${m.year}` : null;
  }, [monthFilter, fyMonths]);

  // Row-click → open the project's job card modal so the user can review
  // project info or jump into the fee plan editor without leaving Budget.
  const [modalProjectId, setModalProjectId] = useState<number | null>(null);
  const [modalRow, setModalRow] = useState<BudgetRow | null>(null);
  // Back from the invoice builder: reopen the modal it was opened from, on
  // the same tab. Driven by the restore snapshot rather than a BudgetRow so
  // it doesn't have to wait for (or depend on) the grid data.
  const { restoredProps, clearRestored } = useRestoredJobCardModal();
  const handleRowOpen = (row: BudgetRow) => {
    clearRestored();
    setModalRow(row);
    setModalProjectId(row.project_id);
  };
  const handleModalClose = () => {
    setModalProjectId(null);
    setModalRow(null);
    clearRestored();
    // The JobCardModal lets the user edit fees / team / general info.
    // We don't get a granular "fee changed" callback, so refetch on close
    // to pick up anything that might have changed (cheap single RPC).
    refetch();
  };
  const modalJobNumber = modalRow?.job_number != null
    ? `${modalRow.job_number}${modalRow.entity_type === 'stage' && modalRow.stage_code ? `.${modalRow.stage_code}` : ''}`
    : '';
  const modalJobCard: JobCard | null = modalRow
    ? {
        project_id: modalRow.project_id,
        project_name: `${modalJobNumber ? modalJobNumber + ' · ' : ''}${modalRow.project_name}`,
        job_number: modalJobNumber,
        workflow_status: modalRow.workflow_status_name,
        project_lead_name: modalRow.project_lead_name ?? '',
        client_name: '',
        allocations: [],
        first_allocation_date: '',
        last_allocation_date: '',
        total_hours: 0,
        total_cost: 0,
        invoices: null,
        total_invoiced: 0,
        invoice_count: 0,
      }
    : null;

  // A status filter (a lifecycle pill, or the status dropdown) scopes the page
  // to project/stage rows in those statuses. The RWC/RWD aggregate rows have
  // no workflow status — they are hand-set annual budgets — so under a status
  // filter they are hidden and left out of the totals, and the footer drops to
  // its Totals row: Allocated / Actual / Target / Variance are whole-business
  // monthly figures, and beside a filtered list they read as though they
  // should add up to it, which they can't.
  const statusFiltered = workflowStatuses.length > 0;

  // Aggregate column totals for the footer. Each row is its own entity now
  // (project, or stage) — sum the per-entity fee/unallocated directly with
  // no project-level dedupe.
  const {
    forecastColumnTotals,
    actualColumnTotals,
    wonFeeSum,
    probableSum,
    unallocatedSum,
    priorColumnTotal,
  } = useMemo(() => {
    const fc = new Array(12).fill(0);
    const ac = new Array(12).fill(0);
    let wonSum = 0;
    let probSum = 0;
    let unallocSum = 0;
    let priorTotal = 0;

    if (data) {
      for (const row of data.rows) {
        wonSum += Number(row.won_fee) || 0;
        probSum += Number(row.probable_fee) || 0;
        unallocSum += Number(row.unallocated_fee) || 0;
        priorTotal += Number(row.prior_amount) || 0;
        row.forecasts.forEach((m, i) => {
          fc[i] += Number(m.amount) || 0;
        });
        row.actuals.forEach((m, i) => {
          ac[i] += Number(m.amount) || 0;
        });
      }
      // Fold the RWC + per-client RWD aggregate rows into the same monthly
      // totals so the footer reflects whole-business numbers, not just
      // Design/Advisory. Annual fee + unallocated are also folded in now that
      // aggregate rows carry a per-FY annual budget. That budget folds into
      // the WON total because it is committed work — nothing is added to
      // probSum, since aggregate rows are hand-set budgets, not fees with
      // awardable phases, and so have no probable component at all.
      for (const agg of statusFiltered ? [] : data.aggregate_rows ?? []) {
        wonSum += Number(agg.total_fee) || 0;
        unallocSum += Number(agg.unallocated_fee) || 0;
        agg.targets.forEach((m, i) => {
          fc[i] += Number(m.amount) || 0;
        });
        agg.actuals.forEach((m, i) => {
          ac[i] += Number(m.amount) || 0;
        });
      }
    }
    return {
      forecastColumnTotals: fc,
      actualColumnTotals: ac,
      wonFeeSum: wonSum,
      probableSum: probSum,
      unallocatedSum: unallocSum,
      priorColumnTotal: priorTotal,
    };
  }, [data, statusFiltered]);

  const handleForecastSaved = (
    row: BudgetRow,
    year: number,
    month: number,
    newAmount: number,
  ) => {
    setData((prev) => {
      if (!prev) return prev;
      // month=0 is the pre-FY tracking lump sum stored on the row itself
      // rather than in the 12-month forecasts array.
      const isPrior = month === 0;

      const updatedRows = prev.rows.map((r) => {
        // Optimistic update only applies to the matching ENTITY
        // (project_id + stage_id). Other entities of the same project are
        // independent and must not be touched.
        const sameEntity =
          r.project_id === row.project_id &&
          (r.stage_id ?? null) === (row.stage_id ?? null);
        if (!sameEntity) return r;

        // Diff against the CURRENT row in state (`r`), not the closed-over
        // `row` prop — a refetch may have refreshed the row since render.
        const oldAmount = isPrior
          ? Number(r.prior_amount ?? 0)
          : (r.forecasts.find((f) => f.year === year && f.month === month)?.amount ?? 0);
        const diff = newAmount - oldAmount;

        // Formula must match the SQL in get_budget_page_data:
        //   unallocated = entity_fee - probable_fee
        //               - sum(all forecasts incl. prior),
        //   floored at 0, and forced to 0 on Complete/Archived entities.
        // probable_fee is a property of the FEE, not of the forecast being
        // typed here, so it carries through this recompute unchanged — only
        // a fee edit (or winning the job) can move it, and both refetch.
        const newAllTimeForecast = Number(r.total_forecast_all_time) + diff;
        const newUnalloc = isCompletedRow(r)
          ? 0
          : Math.max(
              Number(r.entity_fee) - Number(r.probable_fee ?? 0) - newAllTimeForecast,
              0,
            );

        const newForecasts: MonthlyAmount[] = isPrior
          ? r.forecasts
          : r.forecasts.map((f) =>
              f.year === year && f.month === month ? { ...f, amount: newAmount } : f,
            );
        const newPriorAmount = isPrior ? newAmount : Number(r.prior_amount ?? 0);

        return {
          ...r,
          forecasts: newForecasts,
          prior_amount: newPriorAmount,
          total_forecast_all_time: newAllTimeForecast,
          unallocated_fee: newUnalloc,
        };
      });

      return { ...prev, rows: updatedRows };
    });
  };

  const handleAggregateTargetSaved = (
    rowId: number,
    year: number,
    month: number,
    newAmount: number,
  ) => {
    setData((prev) => {
      if (!prev) return prev;
      const updatedAggregateRows = prev.aggregate_rows.map((ar) => {
        if (ar.id !== rowId) return ar;
        const newTargets = ar.targets.map((t) =>
          t.year === year && t.month === month ? { ...t, amount: newAmount } : t,
        );
        // Mirror the SQL: unallocated = total_fee - SUM(targets for this
        // FY), floored at 0.
        const newTargetSum = newTargets.reduce((s, t) => s + Number(t.amount), 0);
        return {
          ...ar,
          targets: newTargets,
          unallocated_fee: Math.max(Number(ar.total_fee) - newTargetSum, 0),
        };
      });
      return { ...prev, aggregate_rows: updatedAggregateRows };
    });
  };

  const handleAggregateFeeSaved = (rowId: number, newAmount: number) => {
    setData((prev) => {
      if (!prev) return prev;
      const updatedAggregateRows = prev.aggregate_rows.map((ar) => {
        if (ar.id !== rowId) return ar;
        const targetSum = ar.targets.reduce((s, t) => s + Number(t.amount), 0);
        return {
          ...ar,
          total_fee: newAmount,
          unallocated_fee: Math.max(newAmount - targetSum, 0),
        };
      });
      return { ...prev, aggregate_rows: updatedAggregateRows };
    });
  };

  const handleAggregateLocalReorder = (orderedIds: number[]) => {
    setData((prev) => {
      if (!prev) return prev;
      // RWC row stays first; reorder the RWD rows to match orderedIds, then
      // append any RWD rows not in the supplied list (shouldn't happen but
      // defensively keeps them visible).
      const rwc = prev.aggregate_rows.filter((r) => r.row_type === 'rwc_all');
      const rwd = prev.aggregate_rows.filter((r) => r.row_type === 'rwd_client');
      const byId = new Map(rwd.map((r) => [r.id, r]));
      const orderedRwd = orderedIds
        .map((id) => byId.get(id))
        .filter((r): r is typeof rwd[number] => r !== undefined);
      const leftovers = rwd.filter((r) => !orderedIds.includes(r.id));
      return { ...prev, aggregate_rows: [...rwc, ...orderedRwd, ...leftovers] };
    });
  };

  const handleBudgetSaved = (year: number, month: number, newAmount: number) => {
    setData((prev) => {
      if (!prev) return prev;
      // Find the index of the saved slot
      const savedIdx = prev.monthly_budget.findIndex((s) => s.year === year && s.month === month);
      if (savedIdx === -1) return prev;
      // Update saved slot + any subsequent inherited slots so the UI mirrors
      // server-side sticky-forward inheritance without waiting for a refetch.
      const updated = prev.monthly_budget.map((slot, idx) => {
        if (idx < savedIdx) return slot;
        if (idx === savedIdx) {
          return { ...slot, budget_amount: newAmount, is_inherited: false };
        }
        if (slot.is_inherited) {
          return { ...slot, budget_amount: newAmount };
        }
        return slot;
      });
      return { ...prev, monthly_budget: updated };
    });
  };

  const labelColsWidth = LABEL_COLS_WIDTH;
  const monthColWidth = MONTH_COL_WIDTH;
  const unallocColWidth = UNALLOC_COL_WIDTH;

  const gridStyle: React.CSSProperties = {
    ['--budget-label-width' as any]: `${labelColsWidth}px`,
    ['--budget-month-width' as any]: `${monthColWidth}px`,
    ['--budget-unalloc-width' as any]: `${unallocColWidth}px`,
  };

  const leadDropdownItems: DropdownItem[] = useMemo(
    () =>
      teamMembers.map((tm) => ({
        id: tm.id,
        label: `${tm.first_name} ${tm.last_name}`.trim(),
        value: tm.id,
      })),
    [teamMembers],
  );
  const workflowStatusDropdownItems: DropdownItem[] = useMemo(
    () =>
      workflowStatusOptions.map((ws) => ({
        id: ws.id,
        label: ws.status_name,
        value: String(ws.id),
      })),
    [workflowStatusOptions],
  );
  // Status-group pills resolve their names to ids once the options load; a
  // pill is "active" when the current status filter is exactly its id set,
  // so a hand-tuned dropdown selection simply leaves every pill unchecked.
  const statusGroupPills = useMemo(() => {
    const idByName = new Map(
      workflowStatusOptions.map((ws) => [ws.status_name, String(ws.id)]),
    );
    // The counts are taken server-side and know nothing of the month-column
    // filter, which narrows the list in the browser. Under it a pill would
    // promise more rows than it shows, so the badges are dropped instead.
    const counts = monthFilter === null ? data?.status_counts : undefined;
    return STATUS_GROUPS.map((g) => ({
      label: g.label,
      ids: g.statuses
        .map((s) => idByName.get(s))
        .filter((v): v is string => v != null),
      // Rows this pill would show. The RPC counts with the status filter
      // lifted, so the numbers hold still whichever pill is selected.
      count: counts ? g.statuses.reduce((n, s) => n + (counts[s] ?? 0), 0) : null,
    }));
  }, [workflowStatusOptions, data?.status_counts, monthFilter]);
  const allStatusesCount = useMemo(() => {
    const counts = monthFilter === null ? data?.status_counts : undefined;
    return counts ? Object.values(counts).reduce((n, c) => n + c, 0) : null;
  }, [data?.status_counts, monthFilter]);
  const isStatusGroupActive = useCallback(
    (ids: string[]) =>
      ids.length > 0 &&
      workflowStatuses.length === ids.length &&
      ids.every((id) => workflowStatuses.includes(id)),
    [workflowStatuses],
  );
  // The segments: All plus one per lifecycle group. The active value is the
  // group whose id set is exactly the current status filter; a hand-tuned
  // dropdown selection matches none, so no segment lights up.
  const statusSegments = useMemo(
    () => [
      { value: 'all', label: 'All', count: allStatusesCount ?? undefined },
      ...statusGroupPills.map((g) => ({
        value: g.label,
        label: g.label,
        count: g.count ?? undefined,
        // Disabled until the status options resolve -- with an empty id set a
        // click would CLEAR the status filter instead of applying the group.
        disabled: g.ids.length === 0,
      })),
    ],
    [statusGroupPills, allStatusesCount],
  );
  const activeStatusSegment =
    workflowStatuses.length === 0
      ? 'all'
      : statusGroupPills.find((g) => isStatusGroupActive(g.ids))?.label ?? '';
  const handleStatusSegment = useCallback(
    (value: string) => {
      if (value === 'all') {
        setFilter('workflowStatuses', []);
        return;
      }
      const group = statusGroupPills.find((g) => g.label === value);
      if (group && group.ids.length > 0) setFilter('workflowStatuses', group.ids);
    },
    [statusGroupPills, setFilter],
  );

  const currentFy = getCurrentFinancialYear();
  const hasActiveFilters =
    searchInput.length > 0 ||
    projectTypes.length > 0 ||
    projectLead !== '' ||
    clientId !== '' ||
    workflowStatuses.length > 0 ||
    monthFilter !== null ||
    fyStartYear !== currentFy;
  const handleClearFilters = useCallback(() => {
    resetFilters();
    setSearchInput('');
    setMonthFilter(null);
  }, [resetFilters]);

  const typeDropdownItems: DropdownItem[] = useMemo(
    () => [
      { id: 'all', label: 'All Types', value: '' },
      ...PROJECT_TYPE_OPTIONS.map((t) => ({ id: t, label: t, value: t })),
    ],
    [],
  );
  const financialYearDropdownItems: DropdownItem[] = useMemo(() => {
    // Earliest selectable FY is 2025 (FY25/26); upper bound is the current
    // FY + 1 (always show next year as a planning slot).
    const earliestFY = 2025;
    const latestFY = Math.max(getCurrentFinancialYear() + 1, earliestFY);
    const items: DropdownItem[] = [];
    for (let y = latestFY; y >= earliestFY; y -= 1) {
      items.push({ id: y, label: getFinancialYearLabel(y), value: y });
    }
    return items;
  }, []);

  return (
    <ListPage
      title="Budget"
      className={`budget-page${showPriorColumn ? ' budget-page--has-prior' : ''}`}
      style={gridStyle}
      search={
        <SearchInput
          placeholder="Search project or job #…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onClear={() => setSearchInput('')}
          aria-label="Search budget"
        />
      }
      segments={
        <StatusSegments
          aria-label="Status group"
          size={isNarrow ? 'sm' : 'md'}
          value={activeStatusSegment}
          onChange={handleStatusSegment}
          segments={statusSegments}
        />
      }
      filters={
        <>
          <FilterTrigger label="Type" active={projectTypes.length > 0}>
            <Dropdown
              items={typeDropdownItems}
              value={projectTypes.length === 1 ? projectTypes[0] : ''}
              onChange={(v) => {
                const next = Array.isArray(v) ? v[0] ?? '' : (v as string) ?? '';
                setFilter('projectTypes', next ? [next] : []);
              }}
              placeholder="All"
            />
          </FilterTrigger>
          <FilterTrigger label="Lead" active={projectLead !== ''}>
            <Dropdown
              items={leadDropdownItems}
              value={projectLead}
              onChange={(v) => {
                const next = Array.isArray(v) ? v[0] ?? '' : (v as string) ?? '';
                setFilter('projectLead', next);
              }}
              placeholder="All"
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Client" active={clientId !== ''}>
            <SearchableClientDropdown
              value={clientId || null}
              onChange={(id) => {
                if (id === null || id === 'none') {
                  setFilter('client', '');
                } else {
                  setFilter('client', String(id));
                }
              }}
              placeholder="All"
              allowNone
              noneLabel="All Clients"
            />
          </FilterTrigger>
          <FilterTrigger label="Status" active={workflowStatuses.length > 0}>
            <Dropdown
              items={workflowStatusDropdownItems}
              value={workflowStatuses}
              onChange={(v) => setFilter('workflowStatuses', v as string[])}
              placeholder="All"
              multiSelect
              searchable
            />
          </FilterTrigger>
          <FilterTrigger label="Year" active={fyStartYear !== currentFy}>
            <Dropdown
              items={financialYearDropdownItems}
              value={fyStartYear}
              onChange={(v) => setFilter('fy', typeof v === 'number' ? v : parseInt(String(v), 10))}
              placeholder="Financial year"
            />
          </FilterTrigger>
          {monthFilter !== null && (
            <button
              type="button"
              className="filter-trigger is-active budget-page__month-chip"
              onClick={() => setMonthFilter(null)}
              title="Clear month filter"
              aria-label={`Clear month filter: ${monthFilterLabel}`}
            >
              <span className="filter-trigger__label">Month</span>
              <span>{monthFilterLabel}</span>
              <span className="budget-page__month-chip-x" aria-hidden="true">×</span>
            </button>
          )}
        </>
      }
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearFilters}
    >
      {error && (
        <div className="budget-page__error">
          <p>Failed to load budget data: {error}</p>
          <button onClick={refetch}>Try Again</button>
        </div>
      )}

      <div className="budget-page__table-wrapper">
        <div className="budget-page__table">
          <div className="budget-page__header-row">
            <div className="budget-page__header-cell budget-page__header-cell--label">
              <div className="budget-page__header-subcell budget-page__header-subcell--project">Project</div>
              <div className="budget-page__header-subcell budget-page__header-subcell--people">Status &amp; Team</div>
              <div
                className="budget-page__header-subcell budget-page__header-subcell--probable"
                title="Fee that is quoted but not yet awarded — either the whole fee on a Probable job, or the phases flagged probable on a won one. Excluded from Unallocated."
              >
                Probable
              </div>
              <div
                className="budget-page__header-subcell budget-page__header-subcell--fee"
                title="Fee we have been awarded — the total fee less anything still probable. This is what Unallocated is measured against."
              >
                Won Fee
              </div>
            </div>
            {showPriorColumn && (
              <div
                className={`budget-page__header-cell budget-page__header-cell--month is-clickable${monthFilter === 'prior' ? ' is-active' : ''}`}
                title="Work completed before FY25/26 tracking started. Click to filter to rows with a Pre-FY25/26 allocation."
                onClick={() => toggleMonthFilter('prior')}
                role="button"
                tabIndex={0}
                aria-pressed={monthFilter === 'prior'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMonthFilter('prior'); }
                }}
              >
                <div className="budget-page__header-month-label">Pre</div>
                <div className="budget-page__header-month-year">FY25/26</div>
              </div>
            )}
            {fyMonths.map((m, idx) => (
              <div
                key={`hdr-${m.year}-${m.month}`}
                className={`budget-page__header-cell budget-page__header-cell--month is-clickable${monthFilter === idx ? ' is-active' : ''}`}
                title={`Click to filter to rows allocated or invoiced in ${m.label} ${m.year}`}
                onClick={() => toggleMonthFilter(idx)}
                role="button"
                tabIndex={0}
                aria-pressed={monthFilter === idx}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleMonthFilter(idx); }
                }}
              >
                <div className="budget-page__header-month-label">{m.label}</div>
                <div className="budget-page__header-month-year">{m.year}</div>
              </div>
            ))}
            <div className="budget-page__header-cell budget-page__header-cell--unalloc">
              Unallocated
            </div>
          </div>

          {loading && (
            <div className="budget-page__loading">
              <p>Loading budget data...</p>
            </div>
          )}

          {!loading && data && data.rows.length === 0 && (
            <div className="budget-page__empty">
              {/* An empty page and an empty FILTER are different problems:
                  telling someone who picked "Construction" to go and create a
                  fee sends them the wrong way. */}
              <p>
                {statusFiltered || projectTypes.length > 0 || projectLead || clientId || searchUrl
                  ? 'No projects or stages match these filters.'
                  : 'No entities to show. Projects and stages only appear here once a fee has been created for them on the Fees register.'}
              </p>
            </div>
          )}

          {!loading && data && data.rows.length > 0 && filteredRows.length === 0 && (
            <div className="budget-page__empty">
              <p>No projects or stages were allocated or invoiced in {monthFilterLabel}.</p>
            </div>
          )}

          {!loading && data && filteredRows.map((row) => (
            <div
              key={`${row.project_id}-${row.stage_id ?? 'p'}`}
              className={`budget-page__row ${row.entity_type === 'stage' ? 'is-stage' : 'is-project'}`}
            >
              <div
                className="budget-page__cell budget-page__cell--label is-clickable"
                onClick={() => handleRowOpen(row)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleRowOpen(row);
                  }
                }}
                title="Open job card"
              >
                {/* Line 1 is the job's identity, line 2 the context that
                    qualifies it (client, and the stage when this is a stage
                    row). Stacking them keeps the whole identity inside 260px
                    where four separate columns needed well over 400px. */}
                <div className="budget-page__cell-project">
                  <div className="budget-page__project-line">
                    <span className="budget-page__job-number">
                      #{row.job_number ?? '—'}{row.entity_type === 'stage' && row.stage_code ? `.${row.stage_code}` : ''}
                    </span>
                    <span className="budget-page__project-name" title={row.project_name}>
                      {row.project_name}
                    </span>
                  </div>
                  <div className="budget-page__project-meta">
                    {row.client_name && (
                      <span className="budget-page__client-name" title={row.client_name}>
                        {row.client_name}
                      </span>
                    )}
                    {row.entity_type === 'stage' && (
                      <span
                        className="budget-page__stage-tag"
                        title={row.stage_description ?? ''}
                      >
                        Stage {row.stage_code}
                        {row.stage_description ? ` — ${row.stage_description}` : ''}
                      </span>
                    )}
                  </div>
                </div>
                {/* Status over people: the pill and the avatars are both short,
                    so stacking them reclaims the 80px the old Lead column
                    spent on a single circle. The lead keeps its ring so it
                    stays distinguishable from the rest of the team. */}
                <div className="budget-page__cell-people">
                  <div className="budget-page__people-status">
                    {row.workflow_status_name ? (
                      <WorkflowStatusPill status={row.workflow_status_name} />
                    ) : (
                      <span className="budget-page__muted">—</span>
                    )}
                  </div>
                  <div className="budget-page__people-avatars">
                    {row.project_lead_name && (
                      <span
                        className="budget-page__lead-initials"
                        title={`${row.project_lead_name} — Project lead`}
                      >
                        {getInitials(row.project_lead_name)}
                      </span>
                    )}
                    {(row.team_members ?? []).slice(0, MAX_TEAM_AVATARS).map((m) => (
                      <span
                        key={m.id}
                        className={`budget-page__team-initials ${memberTypeClass(m.type)}`}
                        title={`${m.first_name} ${m.last_name} — ${m.type}`}
                      >
                        {getMemberInitials(m)}
                      </span>
                    ))}
                    {(row.team_members?.length ?? 0) > MAX_TEAM_AVATARS && (
                      <span
                        className="budget-page__team-overflow"
                        title={(row.team_members ?? [])
                          .slice(MAX_TEAM_AVATARS)
                          .map((m) => `${m.first_name} ${m.last_name} — ${m.type}`)
                          .join('\n')}
                      >
                        +{(row.team_members?.length ?? 0) - MAX_TEAM_AVATARS}
                      </span>
                    )}
                    {!row.project_lead_name && !row.team_members?.length && (
                      <span className="budget-page__muted">—</span>
                    )}
                  </div>
                </div>
                <div
                  className={`budget-page__cell-probable${Number(row.probable_fee) > 0 ? ' is-probable' : ''}`}
                  title={
                    Number(row.probable_fee) > 0
                      ? row.workflow_status_name === 'Probable'
                        ? 'The whole fee is probable — this job has not been won yet'
                        : 'Fee phases flagged probable — quoted, but not yet awarded'
                      : 'No probable fee on this row'
                  }
                >
                  {Number(row.probable_fee) > 0
                    ? formatCurrencyCompact(Number(row.probable_fee))
                    : <span className="budget-page__muted">—</span>}
                </div>
                {/* Won, not total: probable sits in its own column to the
                    left, so the row reads probable + won = the quoted fee,
                    and won − forecasts = unallocated. The full fee is in the
                    tooltip for when it's wanted. */}
                <div
                  className="budget-page__cell-fee"
                  title={`${row.entity_type === 'stage' ? 'Stage' : 'Project'} fee awarded${
                    Number(row.probable_fee) > 0
                      ? ` — ${formatCurrency(Number(row.entity_fee))} quoted, less ${formatCurrency(Number(row.probable_fee))} still probable`
                      : ''
                  }`}
                >
                  {isWhollyProbable(row)
                    ? <span className="budget-page__muted">—</span>
                    : formatCurrencyCompact(Number(row.won_fee))}
                </div>
              </div>
              {showPriorColumn && (
                <div key={`m-${row.project_id}-${row.stage_id ?? 'p'}-prior`} className="budget-page__cell budget-page__cell--month">
                  <MonthlyCell
                    projectId={row.project_id}
                    stageId={row.stage_id}
                    year={FIRST_FY_START_YEAR}
                    month={0}
                    forecast={Number(row.prior_amount ?? 0)}
                    actual={0}
                    onForecastSaved={(y, mo, amt) => handleForecastSaved(row, y, mo, amt)}
                    isPrior
                  />
                </div>
              )}
              {row.forecasts.map((f, idx) => (
                <div key={`m-${row.project_id}-${row.stage_id ?? 'p'}-${f.year}-${f.month}`} className="budget-page__cell budget-page__cell--month">
                  <MonthlyCell
                    projectId={row.project_id}
                    stageId={row.stage_id}
                    year={f.year}
                    month={f.month}
                    forecast={Number(f.amount)}
                    actual={Number(row.actuals[idx]?.amount ?? 0)}
                    onForecastSaved={(y, mo, amt) => handleForecastSaved(row, y, mo, amt)}
                  />
                </div>
              ))}
              <div
                className={`budget-page__cell budget-page__cell--unalloc ${
                  isWhollyProbable(row)
                    ? ''
                    : Number(row.unallocated_fee) > 0
                      ? 'is-positive'
                      : Number(row.unallocated_fee) < 0
                        ? 'is-negative'
                        : ''
                }`}
                title={
                  isWhollyProbable(row)
                    ? 'Nothing won on this row yet — its whole fee is still probable'
                    : 'Won fee not yet allocated to a month'
                }
              >
                {isWhollyProbable(row)
                  ? <span className="budget-page__muted">—</span>
                  : formatCurrency(Number(row.unallocated_fee))}
              </div>
            </div>
          ))}

          {/* The month filter only narrows the project/stage list above; the
              RWC/RWD aggregate rows and the whole-business totals footer stay
              visible as the full reference. A STATUS filter is different — see
              statusFiltered. */}
          {!loading && data && !statusFiltered && (
            <AggregateSection
              rows={data.aggregate_rows ?? []}
              fyStartYear={dataFilters.fyStartYear}
              onTargetSaved={handleAggregateTargetSaved}
              onFeeSaved={handleAggregateFeeSaved}
              onListChanged={refetch}
              onLocalReorder={handleAggregateLocalReorder}
              showPriorColumn={showPriorColumn}
            />
          )}

          {!loading && data && (
            <BudgetTotalRow
              monthlyBudget={data.monthly_budget}
              forecastColumnTotals={forecastColumnTotals}
              actualColumnTotals={actualColumnTotals}
              unallocatedSum={unallocatedSum}
              wonFeeSum={wonFeeSum}
              probableSum={probableSum}
              totalsOnly={statusFiltered}
              onBudgetSaved={handleBudgetSaved}
              showPriorColumn={showPriorColumn}
              priorColumnTotal={priorColumnTotal}
            />
          )}
        </div>
      </div>

      <JobCardModal
        onClose={handleModalClose}
        {...(restoredProps ?? {
          isOpen: modalProjectId !== null,
          jobCard: modalJobCard,
          projectId: modalProjectId ?? undefined,
          stageId: modalRow?.stage_id ?? null,
          initialTab: 'overview' as const,
        })}
      />
    </ListPage>
  );
};

export default Budget;
